// Skill packages (#3033): the specialization mechanism for hosted agents.
// Catalog + draft CRUD + publish + zip import live under /admin/skill-packages;
// install/uninstall/upgrade live under the agent (server-managed pins — the
// wizard's buffered PUT never carries them).

import axios from "axios"

import { api } from "@/lib/api-client"

/**
 * Stable refusal codes `routes/admin/skill_packages.py` sends
 * (`_package_error_response` plus the inline 400/409/503 bodies).
 */
export type SkillPackageErrorCode =
  | "skill_draft_unavailable"
  | "skill_package_conflict"
  | "skill_package_invalid"
  | "skill_package_not_found"
  | "skill_packages_unavailable"

const SERVER_SKILL_PACKAGE_ERROR_CODES = new Set<string>([
  "skill_draft_unavailable",
  "skill_package_conflict",
  "skill_package_invalid",
  "skill_package_not_found",
  "skill_packages_unavailable",
])

// =============================================================================
// Caps, mirrored from `services/skills/packages.py` (#3074)
//
// The editor shows these as budgets and refuses BEFORE the round trip. Every
// value here has exactly one counterpart in the backend model; changing one
// without the other turns a helpful counter into a lie, so keep the pairs
// named identically.
// =============================================================================

/** `NAME_PATTERN` — a lowercase slug, nothing else. */
export const SKILL_PACKAGE_NAME_PATTERN = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/
export const MAX_SKILL_PACKAGE_NAME_LENGTH = 64
export const MAX_SKILL_PACKAGE_DISPLAY_NAME_LENGTH = 120
export const MAX_SKILL_PACKAGE_DESCRIPTION_LENGTH = 2000
export const MAX_SKILL_PACKAGE_INSTRUCTIONS_BYTES = 64 * 1024
export const MAX_SKILL_PACKAGE_FILE_BYTES = 256 * 1024
export const MAX_SKILL_PACKAGE_FILES = 32
export const MAX_SKILL_PACKAGE_TOTAL_BYTES = 2 * 1024 * 1024
export const MAX_SKILL_PACKAGE_DELIVERY_CONTRACT_BYTES = 8 * 1024
export const MAX_SKILL_PACKAGE_GUIDANCE_EXCERPT_BYTES = 2 * 1024
export const MAX_SKILL_PACKAGE_EVAL_CASES = 25
export const MAX_SKILL_PACKAGE_TOOL_RECOMMENDATIONS = 40
export const MAX_SKILL_PACKAGE_REQUIRED_TOOLS = 20
/** `_REFERENCE_SUFFIXES` — the text formats a reference may be. */
export const SKILL_PACKAGE_REFERENCE_SUFFIXES = [
  ".md",
  ".txt",
  ".json",
  ".yaml",
  ".yml",
  ".csv",
  ".html",
  ".xml",
] as const

/** `MAX_DRAFT_DESCRIPTION_LENGTH` / `MIN_DRAFT_DESCRIPTION_LENGTH`. */
export const MAX_SKILL_DRAFT_BRIEF_LENGTH = 4000
export const MIN_SKILL_DRAFT_BRIEF_LENGTH = 12

/**
 * A skill-package refusal the UI can explain.
 *
 * Measured B1/G1: these calls went out on bare axios, so a refused import
 * (name already taken) and a refused delete (the package is installed on an
 * agent) both reached the card as axios's own `"Request failed with status
 * code 409"`. The backend's `{error, code}` body was on the wire the whole
 * time and nothing read it. Same two-part shape the rest of the admin surface
 * uses (`getHostedAgentEvalError`, `getHostedAgentLifecycleErrorCode`): narrow
 * here, translate at the call site.
 *
 * Note both the import collision and the delete-in-use refusal carry the SAME
 * code (`skill_package_conflict`), so the operation — not the code alone —
 * decides the wording; `serverMessage` keeps the backend's own sentence for
 * anything the UI has no phrase for.
 */
export class SkillPackageError extends Error {
  readonly code: SkillPackageErrorCode
  readonly status: number
  /** The backend's own `error` string, when it sent one. */
  readonly serverMessage: string | null

  constructor(init: {
    code: SkillPackageErrorCode
    message: string
    status: number
    serverMessage?: string | null
  }) {
    super(init.message)
    this.name = "SkillPackageError"
    this.code = init.code
    this.status = init.status
    this.serverMessage = init.serverMessage ?? null
  }
}

/** Narrow an unknown error to the typed skill-package refusal, or null. */
export function getSkillPackageError(error: unknown): SkillPackageError | null {
  return error instanceof SkillPackageError ? error : null
}

function rethrowSkillPackageError(error: unknown): never {
  if (!axios.isAxiosError(error) || !error.response) {
    throw error
  }
  const status = error.response.status
  const data = error.response.data as { error?: string; code?: string } | undefined
  const serverMessage = typeof data?.error === "string" ? data.error : null
  const code = data?.code
  if (code && SERVER_SKILL_PACKAGE_ERROR_CODES.has(code)) {
    throw new SkillPackageError({
      code: code as SkillPackageErrorCode,
      message: serverMessage || error.message,
      status,
      serverMessage,
    })
  }
  // Codeless responses that still deserve a specific message.
  if (status === 503) {
    throw new SkillPackageError({
      code: "skill_packages_unavailable",
      message: serverMessage || error.message,
      status,
      serverMessage,
    })
  }
  throw error
}

/** Run a skill-package request, turning its refusal into a `SkillPackageError`. */
async function withSkillPackageErrors<T>(request: () => Promise<T>): Promise<T> {
  try {
    return await request()
  } catch (error) {
    rethrowSkillPackageError(error)
  }
}

export interface SkillPackageToolRecommendations {
  tools: string[]
  tool_actions: Record<string, string[]>
}

export interface SkillPackageContent {
  description: string
  instructions: string
  references: Record<string, string>
  scripts: Record<string, string>
  delivery_contract: string
  guidance_excerpt: string
}

export interface SkillPackageDraft {
  content: SkillPackageContent
  tool_recommendations: SkillPackageToolRecommendations
  required_tools: string[]
  eval_cases: Array<Record<string, unknown>>
}

export interface SkillPackageSummary {
  id: string
  revision: number
  name: string
  display_name: string
  description: string
  status: "active" | "archived"
  latest_version: number
  draft: SkillPackageDraft
  source: "custom" | "platform"
  created_by: string
  created_at: string
  updated_by: string
  updated_at: string
}

export interface SkillPackageCatalog {
  packages: SkillPackageSummary[]
  platform_packages: SkillPackageSummary[]
  builtin_skills: Array<{ name: string; source: "builtin" }>
}

export interface SkillPackageVersionInfo {
  version: number
  content_sha256: string
  published_by: string
  published_at: string
}

export interface InstalledSkillPackage {
  package_id: string
  name: string
  version: number
  content_sha256: string
  installed_at: string
  installed_by: string
}

export interface SkillPackageInstallDiff {
  tools_to_add: string[]
  already_granted: string[]
  tool_actions_changes: Record<string, string[]>
  required_tools_missing: string[]
  requires_load_skill: boolean
}

export interface SkillPackageEvalPreview {
  will_seed: string[]
  will_skip_existing: string[]
  will_skip_over_cap: string[]
  eval_service_available?: boolean
  seeded?: number
}

export interface SkillPackageInstallPreview {
  dry_run: boolean
  package: { package_id: string; name: string; version: number }
  from_version?: number
  diff: SkillPackageInstallDiff
  eval_cases: SkillPackageEvalPreview
  /** A single advisory for the dry run, e.g. the agent cannot run the package's scripts. */
  warning?: string
  /** Advisories for the dry run; older backends send neither field. */
  warnings?: string[]
}

export async function fetchSkillPackageCatalog(): Promise<SkillPackageCatalog> {
  const response = await api.get("/admin/skill-packages")
  const data = response.data || response
  return {
    packages: Array.isArray(data?.packages) ? data.packages : [],
    platform_packages: Array.isArray(data?.platform_packages) ? data.platform_packages : [],
    builtin_skills: Array.isArray(data?.builtin_skills) ? data.builtin_skills : [],
  }
}

export async function fetchSkillPackage(
  packageId: string
): Promise<{ package: SkillPackageSummary; versions: SkillPackageVersionInfo[] }> {
  const response = await api.get(`/admin/skill-packages/${encodeURIComponent(packageId)}`)
  return response.data || response
}

export async function importSkillPackage(input: {
  zipBase64: string
  name?: string
}): Promise<SkillPackageSummary> {
  const response = await withSkillPackageErrors(() =>
    api.post("/admin/skill-packages/import", {
      zip_base64: input.zipBase64,
      ...(input.name ? { name: input.name } : {}),
    })
  )
  return (response.data || response).package
}

export async function createSkillPackage(
  payload: Record<string, unknown>
): Promise<SkillPackageSummary> {
  const response = await withSkillPackageErrors(() => api.post("/admin/skill-packages", payload))
  return (response.data || response).package
}

export async function updateSkillPackage(
  packageId: string,
  payload: Record<string, unknown> & { revision: number }
): Promise<SkillPackageSummary> {
  const response = await withSkillPackageErrors(() =>
    api.put(`/admin/skill-packages/${encodeURIComponent(packageId)}`, payload)
  )
  return (response.data || response).package
}

export async function publishSkillPackage(
  packageId: string,
  revision: number
): Promise<SkillPackageVersionInfo> {
  const response = await withSkillPackageErrors(() =>
    api.post(`/admin/skill-packages/${encodeURIComponent(packageId)}/publish`, { revision })
  )
  return (response.data || response).version
}

/**
 * The AI draft assistant (#3074).
 *
 * Sends the administrator's OWN words and nothing else — the prompt is
 * backend-side platform behaviour, never a client input. The response is an
 * unsaved draft payload for the editor: the server persists nothing and
 * publishes nothing, and `dropped_tools` names any tool the model invented
 * that was thrown away before it could reach an install diff.
 */
export async function draftSkillPackage(input: {
  description: string
  name?: string
}): Promise<{ draft: SkillPackageDraft & SkillPackageDraftIdentity; dropped_tools: string[] }> {
  const response = await withSkillPackageErrors(() =>
    api.post("/admin/skill-packages/draft", {
      description: input.description,
      ...(input.name ? { name: input.name } : {}),
    })
  )
  const data = response.data || response
  return { draft: data.draft, dropped_tools: data.dropped_tools ?? [] }
}

/** The naming fields the generator fills in alongside the draft body. */
export interface SkillPackageDraftIdentity {
  name: string
  display_name: string
  description: string
}

export async function deleteSkillPackage(packageId: string): Promise<void> {
  await withSkillPackageErrors(() =>
    api.delete(`/admin/skill-packages/${encodeURIComponent(packageId)}`)
  )
}

const agentPackagesPath = (agentId: string, packageId?: string) =>
  `/admin/hosted-agents/${encodeURIComponent(agentId)}/skill-packages${
    packageId ? `/${encodeURIComponent(packageId)}` : ""
  }`

export async function installSkillPackage(
  agentId: string,
  body: {
    package_id: string
    version?: number
    apply_tool_recommendations?: boolean
    seed_eval_cases?: boolean
    dry_run?: boolean
  }
): Promise<SkillPackageInstallPreview & { success?: boolean }> {
  const response = await withSkillPackageErrors(() => api.post(agentPackagesPath(agentId), body))
  return response.data || response
}

export async function uninstallSkillPackage(
  agentId: string,
  packageId: string
): Promise<{
  success: boolean
  eval_cases_removed: number
  recommended_tools_still_granted: string[]
}> {
  const response = await withSkillPackageErrors(() =>
    api.delete(agentPackagesPath(agentId, packageId))
  )
  return response.data || response
}

export async function upgradeSkillPackage(
  agentId: string,
  packageId: string,
  body: { to_version?: number; dry_run?: boolean }
): Promise<SkillPackageInstallPreview & { success?: boolean }> {
  const response = await withSkillPackageErrors(() =>
    api.post(`${agentPackagesPath(agentId, packageId)}/upgrade`, body)
  )
  return response.data || response
}
